import { Card, Typography, Empty, Skeleton } from "antd";
import { useTranslation } from "react-i18next";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { LevelCCompetencyStatistics } from "../../types/levelc-reports";
import { useMemo } from "react";

interface LevelCCompetencyChartProps {
  competencies: LevelCCompetencyStatistics[] | undefined;
  overallCompetencies?: LevelCCompetencyStatistics[] | undefined;
  isLoading: boolean;
}

export const LevelCCompetencyChart = ({
  competencies,
  overallCompetencies,
  isLoading,
}: LevelCCompetencyChartProps) => {
  const { t } = useTranslation();

  // Check if we have comparison data (when specific term is selected)
  const hasComparison = overallCompetencies && overallCompetencies.length > 0;

  const chartOptions = useMemo<ApexOptions>(() => {
    if (!competencies || competencies.length === 0) {
      return {};
    }

    // Categories for radar chart (competency names)
    const categories = competencies.map((c) => c.competencyName);

    return {
      chart: {
        type: "radar",
        height: 450,
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: false,
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: false,
          },
        },
        dropShadow: {
          enabled: true,
          blur: 1,
          left: 1,
          top: 1,
        },
      },
      stroke: {
        width: 2,
      },
      fill: {
        opacity: 0.25,
      },
      markers: {
        size: 4,
        hover: {
          size: 6,
        },
      },
      xaxis: {
        categories: categories,
        labels: {
          style: {
            fontSize: "11px",
            colors: categories.map(() => "#666"),
          },
        },
      },
      yaxis: {
        min: 0,
        max: 100,
        tickAmount: 5,
        labels: {
          formatter: (val: number) => `${val}%`,
        },
      },
      plotOptions: {
        radar: {
          size: 160,
          polygons: {
            strokeColors: "#e8e8e8",
            strokeWidth: "1",
            connectorColors: "#e8e8e8",
            fill: {
              colors: ["#f8f8f8", "#fff"],
            },
          },
        },
      },
      colors: hasComparison ? ["#722ED1", "#bfbfbf"] : ["#722ED1"],
      legend: {
        show: true,
        position: "bottom",
        horizontalAlign: "center",
        floating: false,
        fontSize: "12px",
        markers: {
          size: 8,
        },
      },
      tooltip: {
        enabled: true,
        y: {
          formatter: (val: number) => `${val.toFixed(1)}%`,
        },
      },
    };
  }, [competencies, hasComparison]);

  const series = useMemo(() => {
    if (!competencies || competencies.length === 0) {
      return [];
    }

    const result = [
      {
        name: hasComparison 
          ? t("levelCReports.competency.selectedTerm") 
          : t("levelCReports.competency.passRate"),
        data: competencies.map((c) => c.passRate),
      },
    ];

    // Add overall comparison series if available
    if (hasComparison && overallCompetencies) {
      // Map overall data to match the order of competencies
      const overallData = competencies.map((c) => {
        const overall = overallCompetencies.find((o) => o.competencyId === c.competencyId);
        return overall?.passRate ?? 0;
      });

      result.push({
        name: t("levelCReports.competency.allTermsAverage"),
        data: overallData,
      });
    }

    return result;
  }, [competencies, overallCompetencies, hasComparison, t]);

  if (isLoading) {
    return (
      <Card title={t("levelCReports.competency.title")} style={{ marginBottom: 24 }}>
        <Skeleton active paragraph={{ rows: 8 }} />
      </Card>
    );
  }

  if (!competencies || competencies.length === 0) {
    return (
      <Card title={t("levelCReports.competency.title")} style={{ marginBottom: 24 }}>
        <Empty description={t("levelCReports.competency.noData")} />
      </Card>
    );
  }

  return (
    <Card 
      title={
        <Typography.Title level={5} style={{ margin: 0 }}>
          {t("levelCReports.competency.title")}
        </Typography.Title>
      }
      extra={
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {hasComparison 
            ? t("levelCReports.competency.comparisonDescription")
            : t("levelCReports.competency.description")}
        </Typography.Text>
      }
      style={{ marginBottom: 24 }}
    >
      <div style={{ display: "flex", justifyContent: "center" }}>
        <ReactApexChart
          options={chartOptions}
          series={series}
          type="radar"
          height={450}
          width={550}
        />
      </div>
      
      {/* Legend for status colors */}
      <div style={{ display: "flex", justifyContent: "center", gap: 24, marginTop: 16, paddingTop: 16, borderTop: "1px solid #f0f0f0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: "#52c41a" }} />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t("levelCReports.competency.legend.high")} (&gt;70%)
          </Typography.Text>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: "#faad14" }} />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t("levelCReports.competency.legend.medium")} (50-70%)
          </Typography.Text>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: "#ff4d4f" }} />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t("levelCReports.competency.legend.low")} (&lt;50%)
          </Typography.Text>
        </div>
      </div>
    </Card>
  );
};

